import { useCallback, useContext, useEffect, useState } from 'react';

import {
  TrackingContext,
  type TrackingContextValue,
} from '../components/TrackingContext';
import { WWContext, type WWContextValue } from '../components/WWContext';
import { isWwsWeddingWebsiteV2 } from '../types/graphql';
import { getPageNameFromRouteName } from './utils';

const EventNames = {
  photo: 'Photo Inspecting',
  rsvp: 'RSVP Interaction',
  guest: 'Guest Interaction',
  visit: 'Wedding Website Guest Visit',
  leftNav: 'Left Nav Interaction',
};

type Keys = keyof typeof EventNames;

interface TrackCall {
  selection: string;
  userDecisionArea?: string | undefined;
}

function useTracking(
  eventType: Keys,
  routeName: string,
  contextArea?: string | undefined
): (selection: string, trackArea?: string | undefined) => void {
  const { trackingInfo, trackingInfoLoaded } =
    useContext<TrackingContextValue>(TrackingContext);

  const { data, isSinglePageLayout } = useContext<WWContextValue>(WWContext);

  const [trackCalls, setTrackCalls] = useState<TrackCall[]>([]);

  const track = useCallback(
    (selection: string, trackArea?: string | undefined): void => {
      const userDecisionArea = contextArea || trackArea;

      setTrackCalls(prev => {
        return [
          ...prev,
          {
            selection,
            userDecisionArea,
          },
        ]
      });
    },
    [contextArea]
  );

  useEffect((): void => {
    if (isWwsWeddingWebsiteV2(data?.weddingWebsiteResult)) {
      const { message, show: showAnnouncement } =
        data?.weddingWebsiteResult.announcement || {};

      if (trackingInfoLoaded && trackCalls.length > 0) {
        const currentPage = getPageNameFromRouteName(routeName);

        trackCalls.forEach(
          ({ selection, userDecisionArea }: TrackCall): void => {
            if (isWwsWeddingWebsiteV2(data?.weddingWebsiteResult)) {
              window.analytics.track(EventNames[eventType], {
                selection,
                userDecisionArea,
                ...trackingInfo,
                currentPage,
                dateHidden: data?.weddingWebsiteResult?.hideDate || false,
                hasAnnouncement:
                  (Boolean(data?.weddingWebsiteResult?.announcement) &&
                    showAnnouncement) ||
                  false,
                announcementMessage: message || null,
                layout: isSinglePageLayout ? 'single-page' : 'multi-page',
                'build-product': 'guest-services',
              });
            }

            return undefined;
          }
        );

        setTrackCalls([]);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingInfoLoaded, trackCalls]);

  return track;
}

export default useTracking;
